"use client";

import { cn } from "@/utils/cn";
import React from "react";
import { Label, Text } from "react-aria-components";

type Option = { label: string; value: string; disabled?: boolean };

export interface RadioGroupInputProps {
	label?: string;
	labelClassName?: string;
	description?: string;
	errorMessage?: string;
	options?: Option[];
	value?: string;
	onChange: (value: string | null) => void;
	onBlur: () => void;
	className?: string;
	radioClassName?: string;
	children?: React.ReactNode;
	itemsClassName?: string;
}

export const RadioGroupInput = ({
	label,
	labelClassName,
	description,
	errorMessage,
	options,
	value,
	onChange,
	onBlur,
	className,
	radioClassName,
	children,
	itemsClassName = "grid grid-cols-2",
}: RadioGroupInputProps) => {
	const handleChange = (newValue: string) => {
		onChange(value === newValue ? null : newValue);
	};
	return (
		<div role="radiogroup" onBlur={onBlur} className={cn("group flex flex-col py-1 my-1 gap-1.5", className)}>
			{label && <Label className={cn("text-title-medium", labelClassName)}>{label}</Label>}

			<div className={cn("gap-2", itemsClassName)}>
				{options?.length
					? options.map(({ value: curValue, label: optionLabel, disabled }) => {
							const isSelected = value === curValue;

							return (
								<button
									key={curValue}
									type="button"
									role="radio"
									aria-checked={isSelected}
									disabled={disabled}
									onClick={() => !disabled && handleChange(curValue)}
									className={cn(
										"flex items-start gap-2 px-4 py-3 rounded-xl border transition-all text-body w-full min-w-0",
										disabled
											? "opacity-50 cursor-not-allowed border-neutral-200 text-neutral-400 bg-neutral-50"
											: "cursor-pointer",
										isSelected
											? "bg-primary-400"
											: !disabled && "bg-white border-neutral-200 text-neutral-600 hover:border-primary-500",
										"border-primary-600 text-primary-600",
										radioClassName,
									)}
								>
									<span
										className={cn(
											"w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5",
											isSelected ? "border-primary-600" : "border-current",
										)}
									>
										{isSelected && <span className="w-2 h-2 rounded-full bg-current flex-shrink-0" />}
									</span>
									<div
										className="flex-1 min-w-0 text-left"
										style={{
											display: "-webkit-box",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textOverflow: "ellipsis",
											lineHeight: "1.3",
											maxHeight: "2.6em",
											wordBreak: "break-word",
										}}
										title={optionLabel}
									>
										{optionLabel}
									</div>
								</button>
							);
						})
					: children}
			</div>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-1">
				{errorMessage || ""}
			</Text>
		</div>
	);
};
